import React, { useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Edit, Trash2, Plus, X } from 'lucide-react';
import { treatmentPlanComponents, getSectionStyle } from './treatmentPlanRenderers';

/**
 * Auto-resizing textarea for markdown section editing.
 */
const AutoResizeTextarea = ({ value, onChange, onClose }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
      ref.current.focus();
    }
  }, [value]);

  return (
    <div className="space-y-1">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full font-mono text-sm text-gray-800 bg-white border-2 border-brand-400 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none leading-relaxed"
        style={{ minHeight: '120px' }}
      />
      <button
        onClick={onClose}
        className="text-xs text-brand-600 hover:text-brand-800 font-medium"
      >
        Fechar editor
      </button>
    </div>
  );
};

/**
 * A single section card — either rendered markdown (clickable) or a textarea.
 */
const SectionCard = ({ section, index, isEditing, onStartEdit, onContentChange, onCloseEdit, onDelete }) => {
  const style = section.headingLevel > 0 ? getSectionStyle(section.heading) : null;

  if (isEditing) {
    return (
      <div className="border-2 border-brand-400 rounded-xl bg-brand-50 p-4 relative">
        {section.heading && (
          <div className={`text-xs font-semibold mb-2 ${style?.text || 'text-gray-600'}`}>
            {section.heading}
          </div>
        )}
        <AutoResizeTextarea
          value={section.content}
          onChange={(val) => onContentChange(index, val)}
          onClose={onCloseEdit}
        />
        <button
          onClick={() => onDelete(index)}
          className="absolute top-3 right-3 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Excluir seção"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => onStartEdit(index)}
      className="group relative border border-gray-200 rounded-xl p-4 cursor-pointer transition-all hover:bg-brand-50 hover:border-brand-300"
    >
      {/* Hover overlay: pencil icon */}
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(index);
          }}
          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Excluir seção"
        >
          <Trash2 className="h-4 w-4" />
        </button>
        <div className="p-1.5 text-brand-400 bg-brand-50 rounded-lg">
          <Edit className="h-4 w-4" />
        </div>
      </div>

      {/* Rendered markdown */}
      <div className="prose-sm max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={treatmentPlanComponents}>
          {section.content}
        </ReactMarkdown>
      </div>
    </div>
  );
};

/**
 * TreatmentPlanEditor — renders all sections in edit mode.
 *
 * Props:
 *   sections: Array<{id, heading, headingLevel, content}>
 *   editingSectionIndex: number | null
 *   onStartEdit: (index) => void
 *   onCloseEdit: () => void
 *   onContentChange: (index, newContent) => void
 *   onDeleteSection: (index) => void
 *   onAddSection: () => void
 */
const TreatmentPlanEditor = ({
  sections,
  editingSectionIndex,
  onStartEdit,
  onCloseEdit,
  onContentChange,
  onDeleteSection,
  onAddSection,
}) => {
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);

  const handleDelete = useCallback((index) => {
    if (deleteConfirm === index) {
      onDeleteSection(index);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(index);
      // Auto-dismiss after 3 seconds
      setTimeout(() => setDeleteConfirm((prev) => (prev === index ? null : prev)), 3000);
    }
  }, [deleteConfirm, onDeleteSection]);

  return (
    <div className="space-y-3">
      {sections.map((section, index) => (
        <div key={section.id} className="relative">
          {deleteConfirm === index && (
            <div className="absolute -top-2 right-0 z-10 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 shadow-sm flex items-center gap-2 text-xs">
              <span className="text-red-700">Excluir esta seção?</span>
              <button
                onClick={() => handleDelete(index)}
                className="text-red-600 font-semibold hover:text-red-800"
              >
                Sim
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
          <SectionCard
            section={section}
            index={index}
            isEditing={editingSectionIndex === index}
            onStartEdit={onStartEdit}
            onContentChange={onContentChange}
            onCloseEdit={onCloseEdit}
            onDelete={handleDelete}
          />
        </div>
      ))}

      {/* Add Section button */}
      <button
        onClick={onAddSection}
        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Adicionar seção
      </button>
    </div>
  );
};

export default TreatmentPlanEditor;
